import { AfterViewInit, Component, Input, ViewChild, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, NEVER, of, Subscription } from "rxjs";
import { finalize, map, tap, switchMap } from "rxjs/operators";
import { CstDialogResult } from "src/app/common-components/dialogs/cst-dialog-enums";
import { CstDialogsService } from "src/app/common-components/dialogs/cst-dialogs.service";
import { CstFileViewerService } from "src/app/common-components/file-viewers/services/file-viewer.service";
import {
  CstFolderDocumentTreeDatasource,
  CstFolderDocumentTreeNodeType,
  ICstFolderDocumentTreeNode,
  ICstFolderDocumentTreeNodeFolder,
} from "src/app/common-components/folder-document-tree/models";
import { ICstGuiCommand } from "src/app/common-components/list-view/models";
import { CstResponsivePopupService } from "src/app/common-components/resp-popup/services/resp-popup.service";
import { Claims } from "src/app/constants";
import { FileHelper } from "src/app/helpers";
import { CstConnectionService } from "src/app/services/cst-connection.service";
import { UserService } from "src/app/services/user.service";
import {
  DokumentDetailDto,
  InfoportalDokumenteClientProvider,
  InfoportalTagsClientProvider,
  InfoportalFoldersClientProvider,
  TagDto,
  VorgangLookupDto,
  DocumentDeleteCommand,
  DocumentDeleteFolderSource,
  DokumentLookupDetailDto,
  VorgangLookupIncludes,
  VorgangLookupListDto,
  API_BASE_URL,
} from "src/app/web-api/web-api";
import {
  IInfoportalDocumentCreateData,
  InfoportalDocumentCreateComponent,
} from "../../documents/insert/document-create.component";
import { InfoportalFolderCreateComponent } from "../insert/folder-create.component";
import { InfoportalFolderUpdateComponent } from "../update/folder-update.component";
import {
  IInfoportalFoldersFilter,
  InfoportalApiFolderFilterPopupComponent,
} from "./filter-popup/folders-filter-popup.component";
import { StringHelper } from "src/app/helpers/string-helper";
import { CstFolderDocumentTreeSortBy } from "src/app/common-components/folder-document-tree/models/folder-document-tree-sort-by";
import { CstFolderDocumentTreeComponent } from "src/app/common-components/folder-document-tree/component/folder-document-tree.component";

interface ProcessDocumentTreeNode {
  isLoading: boolean;
  isExpanded: boolean;
  isExpandable: boolean;
  level: number;
  name: string;
  description: string;
  id: string;
  lastChanged: Date;
  processID: string;
  internal: boolean;
  filesize?: number;
  tagIDs?: TagDto[];
}

type queryDialogType = null | "create" | "update";

@Component({
    selector: "process-overview",
    templateUrl: "./process-overview.component.html",
    styleUrls: ["./process-overview.component.scss"],
    standalone: false
})
export class ProcessOverViewComponent implements AfterViewInit {
  private _baseUrl = inject(API_BASE_URL);
  private _foldersClient = inject(InfoportalFoldersClientProvider);
  private _documentClient = inject(InfoportalDokumenteClientProvider);
  private _tagsClient = inject(InfoportalTagsClientProvider);
  private _dlg = inject(CstDialogsService);
  _userService = inject(UserService);
  private _popup = inject(CstResponsivePopupService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _connectionService = inject(CstConnectionService);
  private _fileViewerService = inject(CstFileViewerService);

  private _foldersRefreshSub: Subscription;
  private _sortBy: CstFolderDocumentTreeSortBy = "label_asc";
  private _facilityID: number = null;
  @Input() public facilityNoInternal = "";
  @Input() public set facilityID(value: number) {
    this._facilityID = value;
    this.onFoldersRefresh();
  }
  public get facilityID() {
    return this._facilityID;
  }

  @Input() noBorders = false;

  private _dataSource: CstFolderDocumentTreeDatasource = null;

  public filter: IInfoportalFoldersFilter = null;
  public isLoading = false;
  public isDownloadingFolder = false;
  public focusedNode: ICstFolderDocumentTreeNodeFolder = null;

  public folderVisibility = true;
  public selectedHeaderIcon = "folder";

  userClaims = {
    createProcess: false,
    editProcess: false,
    deleteProcess: false,
    viewDocument: false,
    createDocument: false,
    editDocument: false,
    deleteDocument: false,
  };

  public status = "ONLINE";
  public isConnected = true;
  public filtersCombined = [];

  private _folderActions: ICstGuiCommand<ICstFolderDocumentTreeNodeFolder>[] =
    [];

  @ViewChild(CstFolderDocumentTreeComponent)
  docTreeComp: CstFolderDocumentTreeComponent;

  constructor() {
    this._connectionService.monitor().subscribe((isConnected) => {
      this.isConnected = isConnected.hasInternetAccess;
      if (this.isConnected) {
        this.status = "ONLINE";
        if (this._facilityID && this._dataSource.data.length == 0) {
          this.onFoldersRefresh();
        }
      } else {
        this.status = "OFFLINE";
      }
    });
    this.getUserClaims();
    this._subscribeToQueryParams();
    this._buildFolderActions();
  }

  public get userIsExternal() {
    return !this._userService.CurrentUser.internalAccess;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      //// Don't like this timeout thing... setting it here should correctly trigger a sort on the dataSource
      this.sortBy = (sessionStorage.getItem(this._sortByStorageKey) ||
        localStorage.getItem(this._sortByStorageKey) ||
        "label_asc") as CstFolderDocumentTreeSortBy;
    }, 1500);
  }

  private get _sortByStorageKey() {
    return `${this._userService.CurrentUser.mandantID}_InfoportalFolders_SortBy`;
  }

  public set sortBy(value: CstFolderDocumentTreeSortBy) {
    if (this._sortBy === value) {
      return;
    }
    this._sortBy = value;
    localStorage.setItem(this._sortByStorageKey, this._sortBy);
    sessionStorage.setItem(this._sortByStorageKey, this._sortBy);
  }
  public get sortBy() {
    return this._sortBy;
  }

  public get displayFilter() {
    return !this.focusedNode && this.filtersCombined?.length > 0;
  }

  public get user() {
    return this._userService.CurrentUser;
  }

  public onDataSourceInit(dataSource: CstFolderDocumentTreeDatasource) {
    this._dataSource = dataSource;
    this.onFoldersRefresh();
  }

  public onNodeFocusDataSourceInit(event: {
    node: ICstFolderDocumentTreeNodeFolder;
    dataSource: CstFolderDocumentTreeDatasource;
  }) {
    this.isLoading = true;
    event.dataSource.data = [];
    this._documentClient
      .getAll(
        event.node.identifier as string,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        0,
        1000000,
        "Filename asc",
      )
      .pipe(
        finalize(() => (this.isLoading = false)),
        map((result) => {
          return result.data.map((x) => this._DocumentDtoToNode(x));
        }),
      )
      .subscribe((result) => (event.dataSource.data = result));
  }

  public onFocusedNodeChanged(node: ICstFolderDocumentTreeNodeFolder) {
    this.focusedNode = node;
  }

  public onFilterPopupOpen() {
    this._popup
      .open<InfoportalApiFolderFilterPopupComponent, IInfoportalFoldersFilter>(
        InfoportalApiFolderFilterPopupComponent,
        { data: this.filter },
      )
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }
        this.filter = result;
        this._refreshFiltersCombined();
        this.onFoldersRefresh();
      });
  }

  public onFilterRemoveTagID(id: number) {
    if (this.filter?.tagIDs?.length === 0) {
      return;
    }
    this.filter.tagIDs = this.filter.tagIDs.filter((x) => x !== id);
    this.onFoldersRefresh();
  }

  public onItemChanged(item: any) {
    if (
      !(
        item instanceof DokumentLookupDetailDto ||
        item instanceof DokumentDetailDto
      )
    ) {
      this.onFoldersRefresh();
      return;
    }

    this._dataSource.replaceNode(
      this._DocumentDtoToNode(item),
      (x) =>
        x.type === CstFolderDocumentTreeNodeType.Document &&
        x.identifier === item.id,
    );
  }

  public onProgressSortBy(sortBase: string) {
    this.sortBy = (
      !this._sortBy.startsWith(sortBase)
        ? `${sortBase}_asc`
        : this._sortBy.endsWith("asc")
        ? `${sortBase}_desc`
        : `${sortBase}_asc`
    ) as CstFolderDocumentTreeSortBy;
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  private _tags$: BehaviorSubject<TagDto[]> = null;
  public getFilterTagLabelAsync(id: number) {
    if (this._tags$ === null) {
      this._tags$ = new BehaviorSubject([]);
      this._tagsClient
        .getAll(true, false)
        .pipe(
          map((x) => x.data),
          tap((x) => {
            this._tags$.next(x);
          }),
        )
        .subscribe();
    }
    return this._tags$.pipe(
      map((tags) => tags.find((x) => x.id === id)?.tagLabel),
    );
  }

  private getUserClaims(): void {
    this.userClaims.viewDocument =
      this.userIsExternal ||
      this._userService.HasClaim(Claims.InfoportalAPI_DokumentRead);
    if (this.user.internalAccess === false) {
      return;
    }
    this.userClaims.createProcess = this._userService.HasClaim(
      Claims.InfoportalAPI_VorgangCreate,
    );

    this.userClaims.editProcess = this._userService.HasClaim(
      Claims.InfoportalAPI_VorgangUpdate,
    );
    this.userClaims.deleteProcess = this._userService.HasClaim(
      Claims.InfoportalAPI_VorgangDelete,
    );
    this.userClaims.createDocument = this._userService.HasClaim(
      Claims.InfoportalAPI_DokumentCreate,
    );
    this.userClaims.editDocument = this._userService.HasClaim(
      Claims.InfoportalAPI_DokumentUpdate,
    );
    this.userClaims.deleteDocument = this._userService.HasClaim(
      Claims.InfoportalAPI_DokumentDelete,
    );
  }

  public onFoldersRefresh(): void {
    if (!this._facilityID || !this.isConnected || !this._dataSource) {
      return;
    }
    if (this._foldersRefreshSub) {
      this._foldersRefreshSub.unsubscribe();
      this._foldersRefreshSub = null;
    }
    this.isLoading = true;
    this._dataSource.data = [];
    this._foldersRefreshSub = this._refreshPaginatedRecursive(0, 25).subscribe(
      () => {
        this._foldersRefreshSub.unsubscribe();
        this._foldersRefreshSub = null;
      },
    );
  }

  private _refreshPaginatedRecursive(offset: number, pageSize: number) {
    return this._foldersClient
      .getAll(
        this.facilityID,
        undefined,
        undefined,
        undefined,
        VorgangLookupIncludes.Dokumente,
        undefined,
        this.filter?.tagIDs,
        this.filter?.searchText,
        this.filter?.dateRangeBegin,
        this.filter?.dateRangeEnd,
        offset,
        pageSize,
      )
      .pipe(
        this._filterHasAny()
          ? this._refreshMapFiltered()
          : this._refreshMapUnfiltered(),
        finalize(() => (this.isLoading = false)),
        switchMap((result) => {
          for (const folder of result.items) {
            this._dataSource.addNode(folder, false);
          }
          if (
            result.items.length > 0 &&
            result.total > this._dataSource.data.length
          ) {
            return this._refreshPaginatedRecursive(
              result.offset + result.pageSize,
              result.pageSize,
            );
          }
          return NEVER;
        }),
      );
  }

  private _refreshMapUnfiltered() {
    return map((folders: VorgangLookupListDto) => {
      return {
        offset: folders.offset,
        pageSize: folders.pageSize,
        total: folders.total,
        items: folders.data.map((x) => this._FolderDtoToNode(x)),
      };
    });
  }
  private _refreshMapFiltered() {
    return map((folders: VorgangLookupListDto) => {
      const foldersInFilter = folders.data
        .filter((x) => !x.notPartOfFilterResultSet)
        .map((x) => this._FolderDtoToNode(x, true));

      const allFolders = [
        ...foldersInFilter,
        ...folders.data
          .filter((x) => x.notPartOfFilterResultSet)
          .map((x) => this._FolderDtoToNode(x, true)),
      ];

      const result = {
        offset: folders.offset,
        pageSize: folders.pageSize,
        total: folders.total,
        items: foldersInFilter as ICstFolderDocumentTreeNode[],
      };
      for (const folder of folders.data) {
        if (!folder.hasDocuments) {
          continue;
        }
        const folderNode = allFolders.find((x) => x.identifier === folder.id);
        result.items.push(
          ...folder.documents.map((doc) => {
            const node = this._DocumentDtoToNode(doc, true);
            node.folder = folderNode;
            return node;
          }),
        );
      }
      return result;
    });
  }

  private _filterHasAny() {
    if (!this.filter) {
      return false;
    }
    const f = this.filter;
    return (
      f.searchText || f.dateRangeBegin || f.dateRangeEnd || f.tagIDs?.length > 0
    );
  }

  private _FolderDtoToNode(folder: VorgangLookupDto, filteredMode = false) {
    return this._dataSource.createFolder({
      identifier: folder.id,
      label: folder.bezeichnung,
      description: folder.info,
      date: folder.lastChanged,
      icons:
        !folder.intern && !this.userIsExternal
          ? [
              {
                icon: "group",
                tooltip: $localize`:InfoPortal_FolderOverview_FolderTooltip_External:External Folder - This folder is public, because at least one contained document is also displayed to external customer users`,
              },
            ]
          : null,
      children:
        !filteredMode && folder.hasDocuments
          ? folder.documents.map((doc) => this._DocumentDtoToNode(doc))
          : null,
      actions: this._folderActions,
      click: filteredMode
        ? {
            execute: (f) => this._onFilteredFolderClicked(f),
            canExecute: (_) => folder.hasDocuments,
          }
        : null,
    });
  }

  private _DocumentDtoToNode(
    doc: DokumentLookupDetailDto,
    filteredMode = false,
  ) {
    return this._dataSource.createFile({
      identifier: doc.id,
      label: doc.filename,
      description: filteredMode
        ? `${doc.folderPath} - ${doc.bezeichnung}`
        : doc.bezeichnung,
      fileSize: doc.fileSize,
      date: doc.inserted,
      icons:
        !doc.intern && !this.userIsExternal
          ? [
              {
                icon: "group",
                tooltip: $localize`:InfoPortal_FolderOverview_DocumentTooltip_External:External Document - This file is also displayed to external customer users`,
              },
            ]
          : null,
    });
  }

  private _onFilteredFolderClicked(folder: ICstFolderDocumentTreeNodeFolder) {
    this.docTreeComp.onNodeFocus(folder);
  }

  public get isFolderCreateVisible() {
    return this._userService.CurrentUser.internalAccess;
  }
  OnFolderCreate() {
    this._SetQueryDialogType("create", {
      category: "folder",
      facilityID: this.facilityID,
    });
  }

  private _CallFolderCreate() {
    if (!this.isConnected) {
      return;
    }
    const ref = this._popup.open(InfoportalFolderCreateComponent);
    ref
      .afterClosed()
      .pipe(
        finalize(() => {
          this._SetQueryDialogType(null);
        }),
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }
        this.onFoldersRefresh();
      });
  }

  onShowFolder(folderID: string) {
    if (!this.isConnected) {
      return;
    }
    this.onEditFolder(folderID, true);
  }

  onEditFolder(processID: string, readOnlyMode: boolean = false) {
    if (!this.isConnected) {
      return;
    }
    const mode = readOnlyMode === true ? 1 : 0;
    this._SetQueryDialogType("update", {
      category: "folder",
      folderID: processID,
      readonly: mode,
    });
  }

  private _editFolder() {
    const ref = this._popup.open(InfoportalFolderUpdateComponent);
    ref
      .afterClosed()
      .pipe(
        finalize(() => {
          this._SetQueryDialogType(null);
        }),
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }
        this.onFoldersRefresh();
      });
  }

  onChangeFolderVisibility(folderVisibility: boolean) {
    if (!this.isConnected) {
      return;
    }
    switch (folderVisibility) {
      case true:
        return this._onHideFolder();
      case false:
        return this._onUnhideFolder();
    }
  }

  private _onHideFolder() {
    this.folderVisibility = false;
    this.selectedHeaderIcon = "description";
  }

  private _onUnhideFolder() {
    this.folderVisibility = true;
    this.selectedHeaderIcon = "folder";
  }

  onCreateNewDocument(processID: string): void {
    if (!this.isConnected) {
      return;
    }
    this._SetQueryDialogType("create", {
      category: "document",
      folderID: processID,
    });
  }

  private _createNewDocument(folderID: string) {
    const ref = this._popup.open<
      InfoportalDocumentCreateComponent,
      IInfoportalDocumentCreateData,
      DokumentLookupDetailDto[]
    >(InfoportalDocumentCreateComponent, {
      data: { displayInternToggle: true },
    });

    ref
      .afterClosed()
      .pipe(finalize(() => this._SetQueryDialogType(null)))
      .subscribe((result) => {
        if (!result) {
          return;
        }
        for (const doc of result) {
          this._dataSource.addSubNode(
            this._DocumentDtoToNode(doc),
            (n) => n.identifier === folderID,
          );
        }
      });
  }

  canDeleteFolder(node: ICstFolderDocumentTreeNodeFolder) {
    return (
      this.userClaims.deleteProcess &&
      node?.identifier &&
      typeof node.identifier === "string"
    );
  }
  onDeleteFolder(node: ICstFolderDocumentTreeNodeFolder) {
    if (!this.canDeleteFolder(node)) {
      return;
    }
    this._dlg
      .AskYesNo(
        "Sollen der Ordner und eventuell vorhandene zugeordnete Dokumente wirklich gelöscht werden?",
      )
      .pipe(
        switchMap((dlgResult) => {
          if (dlgResult !== CstDialogResult.Yes) {
            return NEVER;
          }
          return this._foldersClient.vorgangDelete(node.identifier as string);
        }),
      )
      .subscribe((_) => {
        this._dataSource.remove(node);
      });
  }

  onShowDocument(documentID: string, tagIDs: any): void {
    if (!this.isConnected) {
      return;
    }
    this.onEditDocument(documentID, tagIDs, true);
  }

  onEditDocument(docID: string, tagIDs: any, readOnlyMode: boolean = false) {
    const mode = readOnlyMode === true ? 1 : 0;
    this._SetQueryDialogType("update", {
      category: "document",
      documentID: docID,
      readonly: mode,
      displayInternToggle: true,
      existingTagIDs: tagIDs,
    });
  }

  onDeleteDocument(documentNode: ProcessDocumentTreeNode): void {
    if (!this.isConnected) {
      return;
    }
    if (!documentNode || !documentNode.id || !documentNode.processID) {
      return;
    }
    this._dlg
      .AskYesNo("Soll das ausgewählte Dokument wirklich gelöscht werden?")
      .subscribe((result) => {
        if (result === CstDialogResult.Yes) {
          this._documentClient
            .documentDelete(
              new DocumentDeleteCommand({
                id: documentNode.id,
                folderID: documentNode.processID,
                folderSource: DocumentDeleteFolderSource.Facility,
              }),
            )
            .subscribe(() => {
              this.onFoldersRefresh();
            });
        }
      });
  }

  canTryOpenDocument() {
    return this.userClaims.viewDocument && this.isConnected;
  }

  onTryOpenDocument(node: ProcessDocumentTreeNode) {
    this._fileViewerService.tryDisplayInfoportalDocument(node.id, node.name);
  }

  canDownloadFolder() {
    return (
      this.userClaims.viewDocument &&
      this.isConnected &&
      !this.isLoading &&
      !this.isDownloadingFolder
    );
  }

  onDownloadFolder(processID: string): void {
    if (!this.canDownloadFolder()) {
      return;
    }
    this.isDownloadingFolder = true;
    this._documentClient
      .dokumentDownloadFolderV2(processID)
      .pipe(finalize(() => (this.isDownloadingFolder = false)))
      .subscribe((result) => {
        FileHelper.DownloadFile(result);
      });
  }

  canDownloadDocument() {
    return this.userClaims.viewDocument && this.isConnected;
  }

  onDownloadDocument(documentID: string): void {
    if (!this.canDownloadDocument()) {
      return;
    }
    this._documentClient.dokumentDownload(documentID).subscribe((r) => {
      FileHelper.DownloadFile(r.data, r.fileName);
    });
  }

  private _subscribeToQueryParams() {
    this._route.queryParams.subscribe({
      next: (params) => {
        if (params.dialogType === "create" && params.category === "folder") {
          this._CallFolderCreate();
        } else if (
          params.dialogType === "update" &&
          params.category === "folder"
        ) {
          this._editFolder();
        } else if (
          params.dialogType === "create" &&
          params.category === "document" &&
          params.folderID
        ) {
          this._createNewDocument(params.folderID);
        }
      },
    });
  }

  private _buildFolderActions() {
    if (!this.userIsExternal) {
      this._folderActions.push(
        {
          label: $localize`:@@ProcessTreeCreateDocument:New Document`,
          execute: (node) =>
            this.onCreateNewDocument(node.identifier as string),
          canExecute: () => this.userClaims.createDocument,
          icon: "note_add",
        },
        {
          label: $localize`:@@ProcessTreeDetails:Details`,
          execute: (node) => this.onShowFolder(node.identifier as string),
          icon: "info",
        },
      );
    }
    if (!this.userIsExternal && this.userClaims.editProcess) {
      this._folderActions.push({
        label: $localize`:@@ProcessTreeEdit:Edit`,
        execute: (node) => this.onEditFolder(node.identifier as string),
        icon: "edit",
      });
    }
    this._folderActions.push({
      label: $localize`:@@Infoportal_Folders_Folder_Download:Download Folder`,
      execute: (node) => this.onDownloadFolder(node.identifier as string),
      canExecute: () => this.canDownloadFolder(),
      icon: "file_download",
    });
    if (!this.userIsExternal) {
      this._folderActions.push({
        label: $localize`:@@ProcessTreeDelete:Delete`,
        execute: this.onDeleteFolder.bind(this),
        canExecute: this.canDeleteFolder.bind(this),
        icon: "delete",
      });
    }
  }

  private _SetQueryDialogType(type: queryDialogType, params?: any) {
    this._router.navigate(["."], {
      relativeTo: this._route,
      queryParams: { dialogType: type, ...params },
      skipLocationChange: false,
      replaceUrl: true,
    });
  }

  private _refreshFiltersCombined() {
    const dateFormatOptions: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };
    const result = [];
    if (this.filter.searchText) {
      result.push({
        label: of(
          StringHelper.truncateIfTooLong(this.filter.searchText, 20, "..."),
        ),
        tooltip: this.filter.searchText,
        onRemove: (_) => {
          this.filter.searchText = null;
          this._refreshFiltersCombined();
          this.onFoldersRefresh();
        },
      });
    }
    if (this.filter.dateRangeBegin) {
      const rangeBeginString = this.filter.dateRangeBegin.toLocaleDateString(
        undefined,
        dateFormatOptions,
      );
      result.push({
        label: of(rangeBeginString),
        tooltip: $localize`:@@InfoportalAPI_FolderTreeFilterBar_DateRangeBegin:Documents or folders created after the ${rangeBeginString}`,
        onRemove: (_) => {
          this.filter.dateRangeBegin = null;
          this._refreshFiltersCombined();
          this.onFoldersRefresh();
        },
      });
    }
    if (this.filter.dateRangeEnd) {
      const rangeEndString = this.filter.dateRangeEnd.toLocaleDateString(
        undefined,
        dateFormatOptions,
      );
      result.push({
        label: of(rangeEndString),
        tooltip: $localize`:@@InfoportalAPI_FolderTreeFilterBar_DateRangeEnd:Documents or folders created before the ${rangeEndString}`,
        onRemove: (_) => {
          this.filter.dateRangeEnd = null;
          this._refreshFiltersCombined();
          this.onFoldersRefresh();
        },
      });
    }
    if (this.filter.tagIDs?.length > 0) {
      for (const tagID of this.filter.tagIDs) {
        result.push({
          label: this.getFilterTagLabelAsync(tagID).pipe(
            map((x) => StringHelper.truncateIfTooLong(x, 20, "...")),
          ),
          onRemove: (_) => {
            this.onFilterRemoveTagID(tagID);
            this._refreshFiltersCombined();
            this.onFoldersRefresh();
          },
        });
      }
    }

    this.filtersCombined = result;
  }
}
